import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'

const initialState = {
  sponsorshipLevels: [],
  sponsorshipLevel: null,
  inProgress: false,
  error: null,
  currentPage: 0,
  pageSize: 10
}

const sponsorshipLevelSlice = createSlice({
  name: 'sponsorshipLevel',
  initialState,
  reducers: {
    setSponsorshipLevels: (state, action) => {
      state.sponsorshipLevels = action.payload
    },
    setSponsorshipLevel: (state, action) => {
      state.sponsorshipLevel = action.payload
    },
    setInProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.sponsorshipLevels = []
      state.sponsorshipLevel = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const getSponsorshipLevels = (conferenceId) => async (dispatch) => {
  try {
    dispatch(sponsorshipLevelSlice.actions.setInProgress(true))
    await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .orderBy('categoryLevel', 'desc')
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const sponsorshipLevels = []
        documents.forEach((doc) => {
          sponsorshipLevels.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(sponsorshipLevelSlice.actions.setSponsorshipLevels(sponsorshipLevels))
        dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
      })
  } catch (err) {
    dispatch(sponsorshipLevelSlice.actions.setError(err))
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  }
}

export const getSponsorshipLevelById = (id) => async (dispatch) => {
  try {
    dispatch(sponsorshipLevelSlice.actions.setInProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).doc(id).get()
    const e = snapshot.data()
    dispatch(
      sponsorshipLevelSlice.actions.setSponsorshipLevel({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  } catch (err) {
    dispatch(sponsorshipLevelSlice.actions.setError(err))
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  }
}

export const addSponsorshipLevel =
  ({ category, categoryLevel }) =>
  async (dispatch, getState) => {
    try {
      const user = getState().auth.user
      const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
        .where('conferenceId', '==', user?.conferenceId)
        .get()

      const sponsorshipLevels = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      const sponsorshipLevelCategories = sponsorshipLevels?.map(({ category }) => category)
      const categoryLevels = sponsorshipLevels?.map(({ categoryLevel }) => categoryLevel)

      if (sponsorshipLevelCategories.includes(category)) throw new Error('Category already exists')
      if (categoryLevels.includes(categoryLevel)) throw new Error('Category level already exists')

      const doc = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).add({
        category,
        categoryLevel,
        conferenceId: user?.conferenceId
      })
      return doc.id
    } catch (err) {
      dispatch(sponsorshipLevelSlice.actions.setError(err))
    }
  }

export const updateSponsorshipLevel =
  ({ category, categoryLevel }, id) =>
  async (dispatch, getState) => {
    try {
      const user = getState().auth.user
      const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
        .where('conferenceId', '==', user?.conferenceId)
        .get()
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        if (data.category === category && doc.id !== id) throw new Error('Category already exists')
        if (data.categoryLevel === categoryLevel && doc.id !== id)
          throw new Error('Category level already exists')
      })
      await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).doc(id).update({
        category,
        categoryLevel
      })
      return id
    } catch (err) {
      dispatch(sponsorshipLevelSlice.actions.setError(err))
    }
  }

export const deleteSponsorshipLevel = (id) => async (dispatch) => {
  try {
    dispatch(sponsorshipLevelSlice.actions.setInProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORS)
      .where('sponsorshipLevelId', '==', id)
      .get()
    if (!snapshot.empty)
      throw new Error('Can not delete this record because it is referenced in Sponsor(s)')
    await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).doc(id).delete()
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  } catch (err) {
    dispatch(sponsorshipLevelSlice.actions.setError(err))
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  }
}

export const resetSponsorshipLevel = () => (dispatch) =>
  dispatch(sponsorshipLevelSlice.actions.setSponsorshipLevel(null))

export default sponsorshipLevelSlice
